// services/inquiryService.ts
import { supabase } from '@/lib/supabaseClient';
import { notificationService } from './notificationService';

interface ProductInquiry {
  leadId: string;
  conversationId: string;
  productType: string;
  specifications: {
    dimensions?: { width: number; height: number; depth?: number };
    material?: string;
    thickness?: number;
    quantity?: number;
    color?: string;
    additionalOptions?: string[];
  };
  estimatedValue?: number;
  urgency: 'low' | 'medium' | 'high' | 'immediate';
  customerBudget?: string;
  deliveryDate?: string;
  notes?: string;
}

export const inquiryService = {
  // Analizuj rozmowę i wyciągnij zapytanie produktowe
  async extractInquiryFromConversation(messages: any[], leadData: any): Promise<ProductInquiry | null> {
    const lastMessages = messages.slice(-10); // Ostatnie 10 wiadomości
    
    // Szukaj kluczowych informacji
    const inquiry: Partial<ProductInquiry> = {
      leadId: leadData.id,
      conversationId: leadData.conversationId,
      urgency: 'medium',
      specifications: {}
    };

    // Analiza wiadomości
    const fullText = lastMessages.map(m => m.content).join(' ').toLowerCase();
    
    // Typ produktu
    if (fullText.includes('ekspozytor')) {
      inquiry.productType = 'ekspozytor';
      if (fullText.includes('lakier')) inquiry.productType = 'ekspozytor_lakiery';
      if (fullText.includes('kosmetyk')) inquiry.productType = 'ekspozytor_kosmetyki';
    } else if (fullText.includes('osłon')) {
      inquiry.productType = 'osłona';
    } else if (fullText.includes('gablon')) {
      inquiry.productType = 'gablota';
    }

    // Wymiary (szukaj wzorców typu "50x30", "500mm x 300mm")
    const dimensionPattern = /(\d+)\s*[xX×]\s*(\d+)(?:\s*[xX×]\s*(\d+))?/g;
    const dimensionMatch = fullText.match(dimensionPattern);
    if (dimensionMatch) {
      const dims = dimensionMatch[0].split(/[xX×]/).map(d => parseInt(d));
      inquiry.specifications!.dimensions = {
        width: dims[0],
        height: dims[1],
        depth: dims[2] || undefined
      };
    }

    // Ilość
    const quantityPattern = /(\d+)\s*(?:szt|sztuk|pcs)/i;
    const quantityMatch = fullText.match(quantityPattern);
    if (quantityMatch) {
      inquiry.specifications!.quantity = parseInt(quantityMatch[1]);
    }

    // Grubość
    const thicknessPattern = /(\d+)\s*mm/i;
    const thicknessMatch = fullText.match(thicknessPattern);
    if (thicknessMatch) {
      inquiry.specifications!.thickness = parseInt(thicknessMatch[1]);
    }

    // Pilność
    if (fullText.includes('pilne') || fullText.includes('szybko') || fullText.includes('jutro')) {
      inquiry.urgency = 'high';
    }
    if (fullText.includes('natychmiast') || fullText.includes('dziś')) {
      inquiry.urgency = 'immediate';
    }

    // Budżet
    const budgetPattern = /(\d+)\s*(?:zł|pln|złotych)/i;
    const budgetMatch = fullText.match(budgetPattern);
    if (budgetMatch) {
      inquiry.customerBudget = budgetMatch[0];
    }

    return inquiry as ProductInquiry;
  },

  // Zapisz zapytanie w bazie
  async saveInquiry(inquiry: ProductInquiry) {
    const { data, error } = await supabase
      .from('product_inquiries')
      .insert({
        lead_id: inquiry.leadId,
        conversation_id: inquiry.conversationId,
        product_type: inquiry.productType,
        specifications: inquiry.specifications,
        estimated_value: inquiry.estimatedValue,
        urgency: inquiry.urgency,
        customer_budget: inquiry.customerBudget,
        delivery_date: inquiry.deliveryDate,
        notes: inquiry.notes,
        status: 'new'
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  // Wyślij szczegółowe powiadomienie o zapytaniu
  async notifyDetailedInquiry(inquiry: ProductInquiry, leadData: any, messages: any[]) {
    // Przygotuj podsumowanie rozmowy
    const conversationSummary = messages
      .slice(-10)
      .map(m => `${m.role === 'user' ? '👤 Klient' : '🤖 AI'}: ${m.content}`)
      .join('\n\n');

    const emailContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <style>
          body { font-family: Arial, sans-serif; background-color: #f3f4f6; margin: 0; padding: 20px; }
          .container { max-width: 800px; margin: 0 auto; background-color: white; border-radius: 8px; box-shadow: 0 2px 4px rgba(0,0,0,0.1); }
          .header { background-color: #1f2937; color: white; padding: 20px; border-radius: 8px 8px 0 0; }
          .urgency-high { background-color: #ef4444; }
          .urgency-immediate { background-color: #dc2626; animation: pulse 2s infinite; }
          @keyframes pulse { 0% { opacity: 1; } 50% { opacity: 0.8; } 100% { opacity: 1; } }
          .content { padding: 30px; }
          .section { margin-bottom: 30px; padding: 20px; background-color: #f9fafb; border-radius: 8px; }
          .label { font-weight: bold; color: #374151; }
          .value { color: #1f2937; margin-left: 10px; }
          .conversation { background-color: #f3f4f6; padding: 15px; border-radius: 8px; font-family: monospace; font-size: 14px; white-space: pre-wrap; }
          .cta-buttons { display: flex; gap: 15px; margin-top: 30px; }
          .cta-button { display: inline-block; padding: 12px 24px; background-color: #f59e0b; color: white; text-decoration: none; border-radius: 6px; font-weight: bold; }
          .cta-secondary { background-color: #6b7280; }
        </style>
      </head>
      <body>
        <div class="container">
          <div class="header ${inquiry.urgency === 'high' || inquiry.urgency === 'immediate' ? `urgency-${inquiry.urgency}` : ''}">
            <h1 style="margin: 0;">🔥 Nowe Zapytanie Produktowe!</h1>
            <p style="margin: 10px 0 0 0; opacity: 0.9;">Pilność: ${inquiry.urgency.toUpperCase()}</p>
          </div>
          
          <div class="content">
            <!-- Dane kontaktowe -->
            <div class="section">
              <h2>👤 Dane Klienta</h2>
              <p><span class="label">Imię i nazwisko:</span><span class="value">${leadData.contact_name}</span></p>
              <p><span class="label">Firma:</span><span class="value">${leadData.company_name || 'Nie podano'}</span></p>
              <p><span class="label">Email:</span><span class="value">${leadData.email}</span></p>
              <p><span class="label">Telefon:</span><span class="value">${leadData.phone || 'Nie podano'}</span></p>
            </div>

            <!-- Szczegóły zapytania -->
            <div class="section">
              <h2>📦 Szczegóły Zapytania</h2>
              <p><span class="label">Produkt:</span><span class="value">${inquiry.productType || 'Nie określono'}</span></p>
              ${inquiry.specifications.dimensions ? `
                <p><span class="label">Wymiary:</span><span class="value">${inquiry.specifications.dimensions.width} x ${inquiry.specifications.dimensions.height} ${inquiry.specifications.dimensions.depth ? `x ${inquiry.specifications.dimensions.depth}` : ''} mm</span></p>
              ` : ''}
              ${inquiry.specifications.quantity ? `
                <p><span class="label">Ilość:</span><span class="value">${inquiry.specifications.quantity} szt.</span></p>
              ` : ''}
              ${inquiry.specifications.thickness ? `
                <p><span class="label">Grubość:</span><span class="value">${inquiry.specifications.thickness} mm</span></p>
              ` : ''}
              ${inquiry.customerBudget ? `
                <p><span class="label">Budżet klienta:</span><span class="value">${inquiry.customerBudget}</span></p>
              ` : ''}
              ${inquiry.deliveryDate ? `
                <p><span class="label">Termin dostawy:</span><span class="value">${inquiry.deliveryDate}</span></p>
              ` : ''}
            </div>

            <!-- Przebieg rozmowy -->
            <div class="section">
              <h2>💬 Przebieg Rozmowy</h2>
              <div class="conversation">${conversationSummary}</div>
            </div>

            <!-- Przyciski akcji -->
            <div class="cta-buttons">
              <a href="http://localhost:5173/crm/leads/${inquiry.leadId}" class="cta-button">
                Otwórz w CRM
              </a>
              <a href="http://localhost:5173/offers/new?leadId=${inquiry.leadId}" class="cta-button cta-secondary">
                Stwórz Ofertę
              </a>
              <a href="tel:${leadData.phone}" class="cta-button cta-secondary">
                Zadzwoń do Klienta
              </a>
            </div>

            <!-- Sugerowane działania -->
            <div class="section" style="background-color: #fef3c7; border: 1px solid #f59e0b;">
              <h3>💡 Sugerowane Działania:</h3>
              <ol>
                <li>Skontaktuj się z klientem w ciągu ${inquiry.urgency === 'immediate' ? '1 godziny' : inquiry.urgency === 'high' ? '2 godzin' : '24 godzin'}</li>
                <li>Przygotuj wycenę w kalkulatorze</li>
                <li>Wyślij ofertę z wizualizacją 3D</li>
                ${inquiry.specifications.quantity && inquiry.specifications.quantity > 10 ? '<li>Rozważ rabat ilościowy</li>' : ''}
              </ol>
            </div>
          </div>
        </div>
      </body>
      </html>
    `;

    // Wyślij email do wszystkich handlowców
    const { data: salespeople } = await supabase
      .from('users')
      .select('email')
      .eq('role', 'salesperson')
      .eq('active', true);

    if (salespeople && salespeople.length > 0) {
      const emails = salespeople.map(sp => sp.email).filter(Boolean);
      
      await fetch('http://localhost:3001/api/send-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to: emails.join(','),
          subject: `🔥 [${inquiry.urgency.toUpperCase()}] Nowe zapytanie - ${inquiry.productType} - ${leadData.contact_name}`,
          html: emailContent
        })
      });
    }

    // Zapisz w systemie powiadomień
    await notificationService.createNotification('system', {
      type: 'offer_request',
      title: `Zapytanie o ${inquiry.productType}`,
      message: `${leadData.contact_name} - ${inquiry.specifications.quantity || '?'} szt.`,
      data: inquiry,
      priority: inquiry.urgency === 'immediate' ? 'urgent' : 'high'
    });
  },

  // Pobierz wycenę z kalkulatora
  async getEstimatedPrice(specifications: any): Promise<number | null> {
    // TODO: Integracja z kalkulatorem
    // Na razie zwracamy szacunkową cenę
    if (specifications.dimensions && specifications.thickness) {
      const area = (specifications.dimensions.width * specifications.dimensions.height) / 1000000; // m²
      const pricePerM2 = specifications.thickness * 50; // Przybliżona cena
      const totalPrice = area * pricePerM2 * (specifications.quantity || 1);
      return Math.round(totalPrice);
    }
    return null;
  }
};